import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "@/ui";
import { cn } from "@/lib/cn";

/* The lowercase wordmark with the sacred coral dot. Never uppercased. */
function Wordmark({ className }: { className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <span className="h-2.5 w-2.5 rounded-full bg-accent" aria-hidden />
      <span className="text-[17px] font-semibold tracking-tight text-ink-strong">
        aigen <span className="font-normal text-muted">Studio</span>
      </span>
    </span>
  );
}

/**
 * Bare auth shell — no app chrome. Two-column split on desktop:
 * left = the form, right = an editorial brand "moment". Single column on
 * mobile. Works in both cream + ink via semantic tokens only.
 */
export function AuthLayout({
  eyebrow,
  title,
  subtitle,
  children,
  footer,
  momentLine,
}: {
  eyebrow: string;
  title: ReactNode;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
  momentLine: ReactNode;
}) {
  return (
    <div className="relative min-h-screen w-full bg-canvas lg:grid lg:grid-cols-[1fr_1.05fr]">
      {/* theme toggle floats top-right, out of the way */}
      <div className="absolute right-5 top-5 z-20">
        <ThemeToggle compact />
      </div>

      {/* ---- left: the form ---- */}
      <div className="flex min-h-screen flex-col px-5 py-8 sm:px-8 lg:min-h-0 lg:px-14 lg:py-12">
        <Link to="/login" className="inline-flex w-fit" aria-label="aigen Studio home">
          <Wordmark />
        </Link>

        <div className="flex flex-1 items-center">
          <div className="mx-auto w-full max-w-sm animate-fade-up py-10">
            <p className="eyebrow mb-3">{eyebrow}</p>
            <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-ink-strong">
              {title}
            </h1>
            <p className="mt-2 text-[15px] leading-relaxed text-muted">{subtitle}</p>

            <div className="mt-8">{children}</div>

            <p className="mt-7 text-[14px] text-muted">{footer}</p>
          </div>
        </div>

        <p className="hidden text-[12px] text-subtle lg:block">
          &copy; 2026 aigen &middot; warm analytics for makers
        </p>
      </div>

      {/* ---- right: the editorial moment (brand-only; one frosted accent) ---- */}
      <aside className="relative hidden overflow-hidden border-l border-hairline bg-paper-2 lg:block">
        {/* subtle warm radial gradient — atmosphere, not noise */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(110% 80% at 78% 12%, rgba(184,68,34,0.14), transparent 58%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(90% 70% at 10% 100%, rgba(200,146,61,0.10), transparent 60%)",
          }}
        />

        <div className="relative flex h-full flex-col justify-between p-14">
          <Wordmark />

          {/* the editorial line — Newsreader italic, the brand promise */}
          <blockquote className="max-w-md">
            <p className="font-serif text-[34px] italic leading-[1.25] text-ink-strong">
              {momentLine}
            </p>
          </blockquote>

          {/* one tasteful static frosted accent — sanctioned brand moment */}
          <div className="glass max-w-xs rounded-card p-5">
            <p className="eyebrow mb-2">this week</p>
            <div className="flex items-baseline gap-2">
              <span className="text-[26px] font-semibold tracking-tight text-ink-strong">
                +18.4%
              </span>
              <span className="flex items-center gap-1.5 text-[13px] text-muted">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-pulse-ring rounded-full bg-accent" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
                </span>
                signups, live
              </span>
            </div>
            <p className="mt-1.5 text-[13px] leading-relaxed text-muted">
              Your studio quietly watches what works, so you can spend your
              attention on what&rsquo;s next.
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}
